import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Roles } from '@tips-sso/shared';
import { PageHeader } from '../components/PageHeader.js';
import { CustomerTabs } from '../components/CustomerTabs.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Field, StatusBadge } from '../components/Field.js';
import { errorMessage } from '../api/client.js';
import {
  fetchCustomerUsers,
  inviteCustomerUser,
  updateCustomerUser,
  type InvitationType,
  type InviteUserInput,
  type OrgMembership,
} from '../api/endpoints.js';

const INVITE_TYPES: InvitationType[] = [
  'customer_user',
  'customer_admin',
  'reseller_user',
  'reseller_admin',
  'tips_internal_user',
];

// Product roles an admin can grant on invite (dangerous platform roles excluded).
const GRANTABLE_ROLES = [
  Roles.CustomerAdmin,
  Roles.CustomerUser,
  Roles.CustomerBillingAdmin,
  Roles.TipsDataQueryUser,
  Roles.TipsDataQueryAdmin,
  Roles.TipsDataExportUser,
  Roles.DatabaseEmailerQueryUser,
  Roles.OutreachSender,
  Roles.OutreachCampaignAdmin,
];

export function CustomerUsersPage() {
  const { id: customerOrgId = '' } = useParams();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const users = useQuery({
    queryKey: ['customerUsers', customerOrgId],
    queryFn: () => fetchCustomerUsers(customerOrgId),
  });

  const setStatus = useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: OrgMembership['status'] }) =>
      updateCustomerUser(customerOrgId, userId, { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customerUsers', customerOrgId] }),
  });

  const columns: Column<OrgMembership>[] = [
    { key: 'email', header: 'Email', render: (u) => u.email },
    { key: 'source', header: 'Source', render: (u) => <span className="badge">{u.membership_source}</span> },
    { key: 'status', header: 'Status', render: (u) => <StatusBadge status={u.status} /> },
    {
      key: 'actions',
      header: '',
      render: (u) => (
        <div className="row-actions">
          {u.status === 'active' ? (
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => setStatus.mutate({ userId: u.zitadel_user_id, status: 'suspended' })}
            >
              Suspend
            </button>
          ) : (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setStatus.mutate({ userId: u.zitadel_user_id, status: 'active' })}
            >
              Reactivate
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Customer Users"
        subtitle={`Org ${customerOrgId}`}
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>
            Invite User
          </button>
        }
      />
      <CustomerTabs customerOrgId={customerOrgId} />

      <DataTable
        columns={columns}
        rows={users.data}
        rowKey={(u) => u.id}
        isLoading={users.isLoading}
        isError={users.isError}
        error={users.error}
        onRetry={() => void users.refetch()}
        emptyMessage="No users in this organization yet."
      />

      <InviteModal
        customerOrgId={customerOrgId}
        open={open}
        onClose={() => setOpen(false)}
        onInvited={() => {
          setOpen(false);
          void qc.invalidateQueries({ queryKey: ['customerUsers', customerOrgId] });
        }}
      />
    </div>
  );
}

function InviteModal({
  customerOrgId,
  open,
  onClose,
  onInvited,
}: {
  customerOrgId: string;
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const [form, setForm] = useState<InviteUserInput>({
    invitedEmail: '',
    invitationType: 'customer_user',
    targetRoles: [],
  });

  const mutation = useMutation({
    mutationFn: () => inviteCustomerUser(customerOrgId, form),
    onSuccess: onInvited,
  });

  function toggleRole(role: string) {
    setForm((f) => ({
      ...f,
      targetRoles: f.targetRoles.includes(role)
        ? f.targetRoles.filter((r) => r !== role)
        : [...f.targetRoles, role],
    }));
  }

  return (
    <Modal open={open} title="Invite User" onClose={onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <div className="form-error">{errorMessage(mutation.error)}</div>}
        <Field label="Email">
          <input
            className="field__input"
            type="email"
            value={form.invitedEmail}
            required
            onChange={(e) => setForm({ ...form, invitedEmail: e.target.value })}
          />
        </Field>
        <Field label="Invitation type">
          <select
            className="field__select"
            value={form.invitationType}
            onChange={(e) =>
              setForm({ ...form, invitationType: e.target.value as InvitationType })
            }
          >
            {INVITE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Roles">
          <div className="toolbar">
            {GRANTABLE_ROLES.map((role) => (
              <label key={role} className="badge" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.targetRoles.includes(role)}
                  onChange={() => toggleRole(role)}
                />{' '}
                {role}
              </label>
            ))}
          </div>
        </Field>
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
